import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Container, Paper, TextField, Button,
  useTheme, alpha, Alert, Grid, InputAdornment,
  IconButton, Divider, CircularProgress
} from '@mui/material';
import {
  LockReset as LockResetIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as CheckCircleIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import axios from 'axios';

const ResetPasswordPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);

  const token = searchParams.get('token');
  const uid = searchParams.get('uid');

  useEffect(() => {
    // Validate that token and uid exist
    if (!token || !uid) {
      setTokenValid(false);
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token, uid]);

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    const passwordError = validatePassword(formData.newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/reset-password`, {
        token,
        uid,
        newPassword: formData.newPassword
      });

      if (response.data.ok) {
        setSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login?passwordReset=true');
        }, 3000);
      } else {
        setError(response.data.error?.message || 'Failed to reset password');
      }
    } catch (err: any) {
      if (err.response?.data?.error?.code === 'INVALID_TOKEN') {
        setError('Invalid or expired reset link. Please request a new password reset.');
        setTokenValid(false);
      } else {
        setError(err.response?.data?.error?.message || 'Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!tokenValid) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4} justifyContent="center">
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                borderRadius: 3,
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  p: 3,
                  background: `linear-gradient(90deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                  color: 'white'
                }}
              >
                <Typography variant="h4" fontWeight={600}>
                  Invalid Reset Link
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>
                  This link is no longer valid
                </Typography>
              </Box>

              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  {error || 'This password reset link is invalid or has expired.'}
                </Typography>
                <Button
                  component={RouterLink}
                  to="/forgot-password"
                  variant="contained"
                  color="secondary"
                  size="large"
                  fullWidth
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    mb: 2,
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  Request New Reset Link
                </Button>
                <Button
                  component={RouterLink}
                  to="/login"
                  variant="outlined"
                  color="primary"
                  size="large"
                  fullWidth
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 500,
                    textTransform: 'none'
                  }}
                >
                  Back to Login
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Grid container spacing={4}>
        {/* Left side - Reset Password Form */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={3}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Form header */}
            <Box
              sx={{
                p: 3,
                background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                color: 'white'
              }}
            >
              <Typography variant="h4" fontWeight={600}>
                {!success ? 'Reset Your Password' : 'Password Reset Complete'}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>
                {!success
                  ? 'Create a strong new password'
                  : 'You can now log in with your new password'
                }
              </Typography>
            </Box>

            {/* Form content */}
            <Box
              sx={{
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1
              }}
            >
              {!success ? (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Enter your new password below. Make sure it's strong and secure.
                  </Typography>

                  {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {error}
                    </Alert>
                  )}

                  <Box component="form" onSubmit={handleSubmit}>
                    <TextField
                      fullWidth
                      label="New Password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.newPassword}
                      onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                      required
                      autoFocus
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      helperText="At least 8 characters with uppercase, lowercase, and number"
                      sx={{ mb: 3 }}
                    />

                    <TextField
                      fullWidth
                      label="Confirm New Password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      required
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                              {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      sx={{ mb: 3 }}
                    />

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      color="secondary"
                      size="large"
                      disabled={loading || !formData.newPassword || !formData.confirmPassword}
                      startIcon={<LockResetIcon />}
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
                        mb: 2
                      }}
                    >
                      {loading ? 'Resetting Password...' : 'Reset Password'}
                    </Button>

                    <Divider sx={{ my: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        OR
                      </Typography>
                    </Divider>

                    <Button
                      component={RouterLink}
                      to="/login"
                      fullWidth
                      variant="outlined"
                      color="primary"
                      sx={{
                        borderRadius: 2,
                        py: 1.5,
                        textTransform: 'none',
                        fontWeight: 500
                      }}
                    >
                      Back to Login
                    </Button>
                  </Box>
                </>
              ) : (
                <Box sx={{ textAlign: 'center' }}>
                  <CheckCircleIcon
                    sx={{
                      fontSize: 80,
                      color: 'success.main',
                      mb: 2,
                      animation: 'pulse 2s infinite'
                    }}
                  />
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Password Reset Successful!
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    Your password has been reset successfully. You can now log in with your new password.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                    Redirecting to login page...
                  </Typography>
                  <CircularProgress sx={{ color: 'secondary.main' }} />
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Right side - Password Requirements */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Welcome back message with gradient */}
            <Paper
              elevation={0}
              sx={{
                mb: 4,
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.1)})`,
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '40%',
                  height: '100%',
                  opacity: 0.1,
                  background: `radial-gradient(circle, ${theme.palette.secondary.main} 0%, transparent 70%)`,
                  transform: 'translateX(30%)'
                }}
              />

              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Typography
                  variant="h3"
                  component="h1"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                    backgroundClip: 'text',
                    textFillColor: 'transparent',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1
                  }}
                >
                  Almost There!
                </Typography>

                <Typography variant="h6" color="text.secondary" paragraph>
                  Choose a strong password to secure your account
                </Typography>

                <Typography variant="body1" color="text.secondary" paragraph>
                  Make sure your new password is unique and not used on other websites.
                </Typography>
              </Box>
            </Paper>

            {/* Password Requirements */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                background: alpha(theme.palette.success.main, 0.05),
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
              }}
            >
              <Typography variant="h6" gutterBottom fontWeight={600} color="success.main">
                Password Requirements
              </Typography>
              <Box component="ul" sx={{ pl: 2, mt: 2, mb: 0 }}>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Minimum 8 characters long
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  At least one uppercase letter (A-Z)
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  At least one lowercase letter (a-z)
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  At least one number (0-9)
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Typography variant="body2" color="text.secondary">
          Remember your password?{' '}
          <RouterLink
            to="/login"
            style={{
              color: theme.palette.primary.main,
              textDecoration: 'none',
              fontWeight: 600
            }}
          >
            Sign in
          </RouterLink>
        </Typography>
      </Box>
    </Container>
  );
};

export default ResetPasswordPage;
